/**
 * Einstellungen ▸ E-Mail-Vorlagen — Zeile „BK und PB per E-Mail
 * benachrichtigen": je ein Dropdown für die Vorlage an die BK und an die PB.
 * Wird beim Anlegen einer Betreuung mit gesetzter Checkbox verwendet
 * (Backend: CareTemplateNotifier). „Keine Vorlage" = Standard-Nachricht.
 * Jede Auswahl wird sofort gespeichert; schlägt das fehl, springt die
 * Auswahl auf den gespeicherten Stand zurück. Ändern nur mit
 * `menu.admin_einstellungen` (wie das PUT im Backend), sonst nur lesbar.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import { usePermission } from '../../contexts/PermissionsContext';
import type { EmailTemplate } from '../../lib/api';
import { careNotifyTemplatesApi, type CareNotifyTemplates } from '../../lib/careNotifyTemplates';

interface Props {
  dark: boolean;
  templates: EmailTemplate[];
}

export function CareNotifyTemplatesRow({ dark, templates }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const canEdit = usePermission('menu.admin_einstellungen');
  const [value, setValue] = useState<CareNotifyTemplates | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    let alive = true;
    careNotifyTemplatesApi.get()
      .then(v => { if (alive) setValue(v); })
      .catch(() => { if (alive) { setValue({ bk: null, pb: null }); setStatus('error'); } });
    return () => { alive = false; };
  }, []);

  const change = async (side: 'bk' | 'pb', raw: string) => {
    const id = raw === '' ? null : Number(raw);
    const previous = value;
    setValue(prev => (prev ? { ...prev, [side]: id } : prev));
    setStatus('saving');
    try {
      setValue(await careNotifyTemplatesApi.update(side, id));
      setStatus('saved');
    } catch {
      setValue(previous);
      setStatus('error');
    }
  };

  const selectStyle: React.CSSProperties = {
    fontSize: 13, padding: '7px 10px', border: `1px solid ${t.border}`, borderRadius: 8,
    background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', minWidth: 200, maxWidth: 280,
  };

  const select = (side: 'bk' | 'pb', label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: t.muted }}>
      {label}
      <select
        aria-label={tr(`settings.email.care_notify.${side}_aria`)}
        value={value?.[side] ?? ''}
        disabled={value === null || !canEdit || status === 'saving'}
        title={canEdit ? undefined : tr('settings.email.care_notify.no_permission')}
        onChange={e => change(side, e.target.value)}
        style={selectStyle}
      >
        <option value="">{tr('settings.email.care_notify.none')}</option>
        {templates.map(tpl => (
          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        margin: '12px 0 4px', padding: '10px 12px', border: `1px solid ${t.borderSoft}`, borderRadius: 10,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{tr('settings.email.care_notify.label')}</span>
      {select('bk', tr('settings.email.care_notify.bk'))}
      {select('pb', tr('settings.email.care_notify.pb'))}
      <span role="status" style={{ fontSize: 12, color: status === 'error' ? '#b42318' : t.muted }}>
        {status === 'saving' && tr('settings.email.care_notify.saving')}
        {status === 'saved' && tr('settings.email.care_notify.saved')}
        {status === 'error' && tr('settings.email.care_notify.error')}
      </span>
    </div>
  );
}
